import React, { useState, useEffect } from 'react';
import { Search, UserPlus, Edit, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Api } from '../../services/Api/Api';
import CreateUserModal from '../../components/Utilisateur/CreateUserModal';
import UpdateUserModal from '../../components/Utilisateur/UpdateUserModal';
import './Utilisateurs.css';

const Utilisateurs = () => {
    const [usersData, setUsersData] = useState({
        count: 0,
        next: null,
        previous: null,
        results: []
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    // Récupérer la liste des utilisateurs avec pagination
    const fetchUsers = async (page = 1, search = '') => {
        try {
            setLoading(true);
            setError(null);

            let endpoint = 'formalite/users-formalite/with-formalite-role';
            const params = new URLSearchParams();

            if (page > 1) {
                params.append('page', page);
            }

            if (search) {
                params.append('search', search);
            }

            if (params.toString()) {
                endpoint += `?${params.toString()}`;
            }

            const response = await Api.get(endpoint);
            setUsersData(response);
            setCurrentPage(page);
        } catch (error) {
            console.error('Erreur lors du chargement des utilisateurs:', error);
            setError('Erreur lors du chargement des utilisateurs');
            setUsersData({ count: 0, next: null, previous: null, results: [] });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers(1, searchTerm);
    }, []);

    // Gérer la recherche avec debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (searchTerm !== '') {
                fetchUsers(1, searchTerm);
            } else {
                fetchUsers(1);
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Filtrer les utilisateurs localement (pour les résultats de la page actuelle)
    const filteredUsers = usersData.results.filter(user => {
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        return fullName.includes(searchLower) ||
            (user.email || '').toLowerCase().includes(searchLower);
    });

    // Gérer la pagination
    const handlePageChange = (direction) => {
        if (direction === 'next' && usersData.next) {
            fetchUsers(currentPage + 1, searchTerm);
        } else if (direction === 'prev' && usersData.previous) {
            fetchUsers(currentPage - 1, searchTerm);
        }
    };

    // Gérer l'ouverture du modal pour créer un utilisateur
    const handleAddUser = () => {
        setShowCreateModal(true);
    };

    // Gérer l'ouverture du modal pour modifier un utilisateur
    const handleEdit = (user) => {
        setSelectedUser(user);
        setShowUpdateModal(true);
    };

    // Gérer le succès des opérations
    const handleModalSuccess = async (message) => {
        alert(message);
        await fetchUsers(currentPage, searchTerm); // Recharger la page actuelle
        setShowCreateModal(false);
        setShowUpdateModal(false);
        setSelectedUser(null);
    };

    // Gérer la fermeture des modals
    const handleCloseModals = () => {
        setShowCreateModal(false);
        setShowUpdateModal(false);
        setSelectedUser(null);
    };

    // Supprimer un utilisateur
    const handleDelete = async (userId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
            try {
                setLoading(true);
                await Api.delete(`formalite/users-formalite/${userId}`);
                await fetchUsers(currentPage, searchTerm); // Recharger la page actuelle
                alert('Utilisateur supprimé avec succès !');
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                alert('Erreur lors de la suppression de l\'utilisateur');
            } finally {
                setLoading(false);
            }
        }
    };

    // Déterminer le rôle d'affichage
    const getUserRole = (user) => {
        if (user.is_staff) return 'Administrateur';
        return 'Utilisateur';
    };

    // Déterminer la couleur du rôle
    const getRoleColor = (user) => {
        if (user.is_staff) return 'blue';
        return 'green';
    };

    if (loading && usersData.results.length === 0) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Chargement des utilisateurs...</p>
            </div>
        );
    }

    return (
        <div className="utilisateurs-container">
            {/* Header */}
            <div className="utilisateurs-header">
                <div className="header-content">
                    <h2>Utilisateurs</h2>
                    <p>Gérez les utilisateurs de la plateforme</p>
                    {error && (
                        <div className="error-message">
                            {error}
                            <button onClick={() => fetchUsers(currentPage, searchTerm)} className="retry-button">
                                Réessayer
                            </button>
                        </div>
                    )}
                </div>

                <div className="header-actions">
                    <div className="search-container">
                        <Search size={20} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Rechercher un utilisateur..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <button className="add-button" onClick={handleAddUser} disabled={loading}>
                        <UserPlus size={20} />
                        Ajouter un utilisateur
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>Nom</th>
                            <th>Email</th>
                            <th>Téléphone</th>
                            <th>Rôle</th>
                            <th>Statut</th>
                            <th>Entreprise</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="no-data">
                                    {searchTerm ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
                                </td>
                            </tr>
                        ) : (
                            filteredUsers.map((user) => (
                                <tr key={user.id}>
                                    <td>
                                        <div>

                                            <span className="user-name">
                                                {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="user-email">{user.email || 'N/A'}</td>
                                    <td>{user.phone || 'N/A'}</td>
                                    <td>
                                        <span className={`role-badge role-${getRoleColor(user)}`}>
                                            {getUserRole(user)}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                                            {user.is_active ? 'Actif' : 'Inactif'}
                                        </span>
                                    </td>
                                    <td>{user.company?.name || 'Aucune'}</td>
                                    <td>
                                        <div className="actions-menu">
                                            <button
                                                className="action-button"
                                                onClick={() => handleEdit(user)}
                                                title="Modifier"
                                                disabled={loading}
                                            >
                                                <Edit size={16} />
                                                Modifier
                                            </button>
                                            <button
                                                className="action-button delete-button"
                                                onClick={() => handleDelete(user.id)}
                                                title="Supprimer"
                                                disabled={loading}
                                            >
                                                <Trash2 size={16} />
                                                Supprimer
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="pagination-container">
                <div className="pagination-info">
                    <span>
                        Total : {usersData.count} utilisateur(s)
                        {usersData.count > 0 && (
                            <span> - Page {currentPage}</span>
                        )}
                    </span>
                </div>

                <div className="pagination-controls">
                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange('prev')}
                        disabled={!usersData.previous || loading}
                    >
                        <ChevronLeft size={16} />
                        Précédent
                    </button>

                    <button
                        className="pagination-button"
                        onClick={() => handlePageChange('next')}
                        disabled={!usersData.next || loading}
                    >
                        Suivant
                        <ChevronRight size={16} />
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="users-stats">
                <div className="stat-item">
                    <span className="stat-label">Total utilisateurs</span>
                    <span className="stat-value">{usersData.count}</span>
                </div>
                <div className="stat-item">
                    <span className="stat-label">Utilisateurs actifs</span>
                    <span className="stat-value">
                        {usersData.results.filter(u => u.is_active).length}
                    </span>
                </div>
                <div className="stat-item">
                    <span className="stat-label">Administrateurs</span>
                    <span className="stat-value">
                        {usersData.results.filter(u => u.is_staff).length}
                    </span>
                </div>
            </div>

            {/* Modals */}
            <CreateUserModal
                isOpen={showCreateModal}
                onClose={handleCloseModals}
                onSuccess={handleModalSuccess}
            />

            <UpdateUserModal
                isOpen={showUpdateModal}
                onClose={handleCloseModals}
                onSuccess={handleModalSuccess}
                user={selectedUser}
            />
        </div>
    );
};

export default Utilisateurs;